import { useEffect, useRef, memo } from 'react';
import RenderQaRecord from '@/containers/qa-drawer/components/qa-records';
import WhetherSolve from '@/containers/qa-drawer/components/whether-solve';
import QuestionComment from '@/components/question-comment';
import AnimationContainer from '@/components/animation-container';
import { NewCreateQuestionSvg } from '@/components/svg-icons/dialogue-area';
import { getQaStore, useQaStore } from '@/store/qa';
import { getGlobalStore } from '@/store/global';
import { environment } from '@gaodun.com/gtools';
import { createQaRecordId, getUuid, toastInQa, transformCharToMessage } from '@/utils/qa';
import { check_transform_to_teacher_permission, luca_ask_resolved_state } from '@/apis/qa';
import { createNewQuestion, getTransformToTeacherParams } from '@/containers/qa-drawer/index.helper';
import { ISolveStatus } from '@/types/models/qa';
import {
    disconnectGPTAfter,
    IReadStreamParams,
    ICreateRecordOptions,
    disableOcrEditOrReplace,
    getResponseOfGPT,
    IGetResponseOfGPTParams,
    getResponseOfCreateLucaRecord,
    excuteQuestionSearchProcess,
    excuteGuessYouAskProcess,
    ISendToGPTOptions,
} from './index.helper';
import { delay, delayExcuteCallback } from '@/utils';
import { useDebounceFn, useThrottleFn } from 'ahooks';
import { postOtherQuestion } from '@/apis';
import ReplyReminder, { IReplyReminderRef } from './reply-reminder';
import { createSubjectSearchCardSensors, clickSubjectSearchButtonSensors } from '@/utils/sensors';
import './index.scss';

const DialogueArea = () => {
    const { publish } = getGlobalStore()!;
    const {
        qaConfig,
        qaRecords,
        updateQaConfig,
        pushQaRecords,
        deleteLastQaRecord,
        updateRecordIdOfLastUserMessage,
        setLockStatus,
        clearLockStatus,
        filterQaRecords,
        deleteQaRecordByRecordId,
        openExcuteTimer,
        getExcuteProcessId,
        isContinueExcute,
        deleteExcuteRecord,
        isAllowScrollToBottom,
    } = useQaStore();
    const {
        lucaAskChatId,
        questionState,
        lucaQuestionAgainNum = 0,
        teacherQuestionAgainNum = 0,
        teacherAnswerTime,
        appraise,
        appraiseState,
        askId,
        showWhetherSolve,
        showCreateNewQaBtn,
        tipsContent: whetherSolveTitle,
        subjectSearchOnOff,
    } = qaConfig;
    // 当模型返回functioncall时，延时返回流程执行中，如果重新初始化或者关闭聊天组件，则阻止后续程序执行
    const preventFunctionCall = useRef<boolean>(false);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const streamReader = useRef<ReadableStreamDefaultReader<Uint8Array>>();
    const abortController = useRef<AbortController>();
    const disconnectStreamReason = useRef<'initiative' | 'switchChat'>();
    const replyReminderRef = useRef<IReplyReminderRef>();

    // 流式展示追问次数用完的提示
    const showLastQaTips = async (message = '') => {
        const content = '你的追问次数已用完，若还未解决你的疑问，请点击下方【未解决】按钮，将问题提交给名师回答';
        await replyReminderRef.current!.simulateStreamReply(message || content);
        pushQaRecords({
            recordId: createQaRecordId('respondent'),
            role: 'respondent',
            type: 'qaNumUsedUp',
            content,
        });
    };

    // 评价完成
    const handleAppraiseSuccess = () => {
        updateQaConfig({ appraiseState: 1 });
        filterQaRecords({ qaNumUsedUp: true });
    };

    // 流结束
    const streamDone = (params: IReadStreamParams) => {
        const { isCreateQa, lucaAskChatId, lucaRecordId, question, answer, functionCall, toolCalls, botChatIdOfStream } = params || {};
        if (answer) {
            disconnectStreamReason.current && disconnectGPTAfter({ recordId: lucaRecordId, botChatIdOfStream, question, answer });
            disconnectStreamReason.current !== 'switchChat' &&
                pushQaRecords(
                    { recordId: lucaRecordId, role: 'respondent', type: 'ai', content: answer, permanentActionBar: true },
                    { resetPermanentActionBarStatus: true }
                );
        }
        // 是否有functionCall
        if (functionCall) {
            console.log('functionCall', functionCall);
            return new Promise(resolve => {
                setTimeout(() => {
                    if (preventFunctionCall.current) {
                        preventFunctionCall.current = false;
                        return;
                    }
                    resolve(sendToGPT({ role: 'function', content: question, lucaAskChatId }));
                }, 300);
            });
        }
        if (toolCalls.length) {
            console.log('toolCalls', toolCalls);
            return new Promise(resolve => {
                setTimeout(() => {
                    if (preventFunctionCall.current) {
                        preventFunctionCall.current = false;
                        return;
                    }
                    resolve(sendToGPT({ role: 'function', content: question, lucaAskChatId }));
                }, 300);
            });
        }
        // 模型返回超时熔断处理
        if (!answer && !disconnectStreamReason.current) {
            pushQaRecords({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'retry',
                question: params.question,
                retryType: isCreateQa ? 1 : 2,
            });
        }
        return { success: true, saveGuessYouAsk: true };
    };

    // 递归读流
    const readStream = async (params: IReadStreamParams): Promise<any> => {
        const {
            isCreateQa,
            lucaRecordId,
            preStreamMessage: preChar = '',
            answer: preAnswerContent,
            functionCall: preFunctionCall,
            toolCalls: preToolCalls,
        } = params;
        const { value, done } = await streamReader.current!.read();
        if (done) return streamDone(params);
        const char = new TextDecoder('utf-8').decode(value, { stream: true });
        console.log('char', char);
        const {
            type,
            content = '',
            functionCall,
            toolCalls = [],
            preStreamMessage,
        } = transformCharToMessage(preChar + char, { preAnswerContent, preFunctionCall, preToolCalls });
        // console.log('content', type, content);
        const newParams = { ...params, answer: content, functionCall, toolCalls, preStreamMessage };
        switch (type) {
            case 'text':
                if (!content) return readStream(newParams);
                if (!preAnswerContent) publish('DialogueTool', { type: 'showStop' });
                replyReminderRef.current?.insertStreamText(content);
                break;
            case 'function_call':
            case 'tool_calls':
                if (content) {
                    publish('DialogueTool', { type: 'hideStop' });
                    pushQaRecords(
                        { recordId: lucaRecordId, role: 'respondent', type: 'ai', content, permanentActionBar: true },
                        { resetPermanentActionBarStatus: true }
                    );
                    replyReminderRef.current?.showLoading('lucaThinking');
                    newParams.answer = '';
                }
                break;
            case 'incomplete_answer':
                publish('DialogueTool', { type: 'showContinue' });
                return streamDone(newParams);
            case 'sensitive_words_replace':
                return streamDone(newParams);
            case 'retry':
                pushQaRecords({
                    recordId: createQaRecordId('respondent'),
                    role: 'respondent',
                    type: 'retry',
                    question: newParams.question,
                    retryType: isCreateQa ? 1 : 2,
                });
                return { success: true, saveGuessYouAsk: false };
            case 'recharge':
                pushQaRecords({
                    recordId: createQaRecordId('respondent'),
                    role: 'respondent',
                    type: 'rechargeLucaQa',
                });
                return { success: true, saveGuessYouAsk: false };
            case 'no_ask_again_num':
            case 'stream_error':
                await replyReminderRef.current!.simulateStreamReply(content);
                pushQaRecords({
                    recordId: lucaRecordId || createQaRecordId('respondent'),
                    role: 'respondent',
                    type: type === 'no_ask_again_num' ? 'qaNumUsedUp' : 'streamTips',
                    content,
                });
                if (type === 'no_ask_again_num') {
                    updateQaConfig({ lucaQuestionAgainNum: 0 });
                    return { success: false, saveGuessYouAsk: false, saveQuestionSearch: false };
                }
                return { success: true, saveGuessYouAsk: false };
            case 'toast_error':
            case 'modal_error':
                type === 'modal_error' ? publish('WarningModal', { type: 'normal', content }) : toastInQa(content);
                deleteLastQaRecord();
                publish('DialogueTool', { type: 'echo', content: params.question, images: params.images });
                return { success: false, saveGuessYouAsk: false };
            case 'token_fail':
                location.href = `https://${environment.getApiEnv()}v.gaodun.com`;
                return { success: false, saveGuessYouAsk: false };
            default:
                break;
        }
        return readStream(newParams);
    };

    // 发送GPT
    const sendToGPT = async (params: IGetResponseOfGPTParams, options?: ISendToGPTOptions) => {
        try {
            const excuteId = getExcuteProcessId();
            abortController.current = new AbortController();
            const {
                isCreateQa,
                question,
                newestLucaAskChatId,
                userRecordId,
                lucaRecordId,
                isLastAskAgain,
                botChatIdOfStream,
                needUpdateUserRecordId,
                needRefineProblem,
                reader,
            } = await getResponseOfGPT(params, abortController.current);
            openExcuteTimer();
            const questionSearchProcessFn = excuteQuestionSearchProcess(
                { lucaAskChatId: newestLucaAskChatId, question },
                { isCreateQa, needRefineProblem, subjectSearchOnOff, mode: options?.mode, excuteId, ...(replyReminderRef.current || {}) }
            );
            const guessYouAskProcessFn = excuteGuessYouAskProcess(
                { lucaAskChatId: newestLucaAskChatId },
                { needCreate: needUpdateUserRecordId, ...(replyReminderRef.current || {}) }
            );
            needUpdateUserRecordId && updateRecordIdOfLastUserMessage(userRecordId);
            streamReader.current = reader;
            const { success, saveGuessYouAsk, saveQuestionSearch } = await readStream({
                question,
                images: params.images,
                lucaRecordId,
                answer: '',
                toolCalls: [],
                returnSuccess: true,
                lucaAskChatId: newestLucaAskChatId!,
                botChatIdOfStream,
                isCreateQa,
            });
            streamReader.current = undefined;
            abortController.current = undefined;
            return {
                success,
                saveGuessYouAsk,
                saveQuestionSearch,
                newestLucaAskChatId,
                isLastAskAgain,
                isCreateQa,
                excuteId,
                question,
                questionSearchProcessFn,
                guessYouAskProcessFn,
            };
        } catch (error) {
            return { success: false };
        }
    };

    // 是否解决状态变更
    const handleSolve = async (state: ISolveStatus) => {
        const excuteId = getExcuteProcessId();
        const { qaConfig } = getQaStore()!;
        const { status, message } = await luca_ask_resolved_state({ lucaAskChatId: qaConfig.lucaAskChatId!, state });
        if (status !== 0) {
            toastInQa(message || '');
            return;
        }
        if (!isContinueExcute(excuteId)) {
            return;
        }
        updateQaConfig({ questionState: state, showWhetherSolve: false });
        if (state === 2) {
            publish('DialogueTool', { type: 'reset' });
            pushQaRecords(
                { recordId: createQaRecordId('system'), role: 'system', type: 'whetherSolve', state, title: qaConfig.tipsContent },
                { filterRecordType: { retry: true, qaNumUsedUp: true, guessYouAsk: true } }
            );
            return;
        }
        pushQaRecords(
            { recordId: createQaRecordId('system'), role: 'system', type: 'whetherSolve', state, title: qaConfig.tipsContent },
            { filterRecordType: { retry: true } }
        );
        const { result: permission } = await check_transform_to_teacher_permission({ lucaAskChatId: qaConfig.lucaAskChatId! });
        if (!isContinueExcute(excuteId)) return;
        if (permission) {
            const { success } = await createQaRecord(getTransformToTeacherParams(), { needErrorHandle: false, excuteId });
            if (success) return;
        }
        if (!isContinueExcute(excuteId)) return;
        const content = '很抱歉，未能解决你的疑问。你可以将问题提交给名师，获得专业回答';
        await replyReminderRef.current!.simulateStreamReply(content);
        pushQaRecords([
            { recordId: createQaRecordId('respondent'), role: 'respondent', type: 'goToTeacherQaTips', content },
            {
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'goToTeacherQa',
                teacherAnswerTime: teacherAnswerTime!,
            },
        ]);
    };

    // 创建大接口
    const createQaRecord = async (params: any, options: ICreateRecordOptions = {}) => {
        // 相似问题/OCR识别提问/学科搜索是否属于首次提问，创建问题
        const isCreateQa = !lucaAskChatId && [21, 40, 65].includes(params.recordType);
        const { needErrorHandle = true, requestFinish, excuteId } = options;
        const result = await getResponseOfCreateLucaRecord(lucaAskChatId, params, { needErrorHandle });
        const { success = true, records = [], newestLucaAskChatId = '', guessYouAskParams } = result;
        // 请求完成，目前用于OCR识别结果
        requestFinish?.(success);
        if (!success || (excuteId && !isContinueExcute(excuteId))) return { ...result, isCreateQa };
        let guessYouAskProcessFn = () => Promise.resolve();
        let internalExcuteId = '';
        switch (params.recordType) {
            case 19:
                {
                    // 若点击“未解决”后，直接转名师，则逐字提示，这里使用needErrorHandle参数来区分
                    if (!needErrorHandle) {
                        const content = '很抱歉，未能解决你的疑问。已将问题提交给名师解答，请耐心等待～';
                        await replyReminderRef.current!.simulateStreamReply(content);
                        pushQaRecords({ recordId: createQaRecordId('respondent'), role: 'respondent', type: 'goToTeacherQaTips', content });
                    }
                    pushQaRecords(records[1], {
                        filterRecordType: {
                            retry: true,
                            qaNumUsedUp: true,
                            // 若点击“未解决”后，直接转名师，不清空逐字提示，这里使用needErrorHandle参数来区分
                            goToTeacherQaTips: needErrorHandle,
                            goToTeacherQa: true,
                            rechargeTeacherQa: true,
                            guessYouAsk: true,
                        },
                    });
                    updateQaConfig({
                        transferLaborState: 1,
                        questionState: 4,
                        showSearchQuestionsByOcrBtn: false,
                        showSubjectSearchLabel: false,
                    });
                    publish('DialogueTool', { type: 'reset' });
                }
                break;
            case 21:
            case 65:
                publish('NavgationBar', {
                    type: 'refineProblem',
                    content: params.text.content,
                    lucaAskChatId: newestLucaAskChatId,
                    isCreateQa,
                });
                break;
            case 40: // OCR识别
                {
                    const ocrContent = params.text.content;
                    if (ocrContent) {
                        publish('NavgationBar', {
                            type: 'refineProblemFromOcr',
                            content: ocrContent,
                            lucaAskChatId: newestLucaAskChatId,
                            isCreateQa,
                        });
                        internalExcuteId = getExcuteProcessId();
                        openExcuteTimer();
                        guessYouAskProcessFn = excuteGuessYouAskProcess(
                            { lucaAskChatId: newestLucaAskChatId, guessYouAskParams },
                            { ...(replyReminderRef.current || {}) }
                        );
                    }
                    const lastItem = records.pop()!;
                    pushQaRecords(records, {
                        filterRecordType: {
                            retry: true,
                            whetherSolve: true,
                            goToTeacherQaTips: true,
                            goToTeacherQa: true,
                            guessYouAsk: true,
                        },
                    });
                    await delay(500);
                    if (lastItem.type === 'streamTips') {
                        await replyReminderRef.current!.simulateStreamReply(lastItem.content);
                    }
                    pushQaRecords(lastItem);
                }
                break;
            case 46: // 更换OCR识别结果搜到的题目
                internalExcuteId = getExcuteProcessId();
                openExcuteTimer();
                guessYouAskProcessFn = excuteGuessYouAskProcess(
                    { lucaAskChatId: newestLucaAskChatId, guessYouAskParams },
                    { ...(replyReminderRef.current || {}) }
                );
                break;
            default:
                // 名师追问不需要渲染
                if (![22].includes(params.recordType)) pushQaRecords(records);
                break;
        }
        return {
            ...result,
            newestLucaAskChatId: newestLucaAskChatId || lucaAskChatId,
            guessYouAskProcessFn,
            excuteId: internalExcuteId,
            isCreateQa,
        };
    };

    // 老系统的答疑
    const createQaRecordOfOldSystem = async (params: any) => {
        const { status } = await postOtherQuestion({
            askId: params.askId,
            images: params.images.length > 0 ? JSON.stringify(params.images) : '',
            questionContent: params.text.content,
        });
        if (status !== 0) return { success: false };
        return { success: true, needDeleteQuestion: true };
    };

    // 滚动到底部 节流
    const { run: handleScrollToBottomByThrottle } = useThrottleFn(
        () => {
            scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight });
        },
        { wait: 100 }
    );

    // 滚动到底部 延时
    const { run: handleScrollToBottomByDebounce } = useDebounceFn(
        () => {
            if (!isAllowScrollToBottom()) return;
            scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight });
        },
        { wait: 100 }
    );

    useEffect(() => {
        const { subscribe } = getGlobalStore()!;
        subscribe('DialogueArea', async ({ type, mode, content, images, params, retryType, finish, recordId }) => {
            switch (type) {
                case 'luca':
                case 'continue':
                case 'retry':
                    {
                        preventFunctionCall.current = false;
                        setLockStatus('replying');
                        replyReminderRef.current?.showLoading('analysising');
                        const {
                            success,
                            saveGuessYouAsk,
                            saveQuestionSearch,
                            newestLucaAskChatId,
                            isLastAskAgain,
                            isCreateQa,
                            questionSearchProcessFn,
                            guessYouAskProcessFn,
                            excuteId,
                        } = await sendToGPT(
                            {
                                lucaAskChatId,
                                content,
                                images,
                                isContinue: type === 'continue',
                                retryType,
                            },
                            { mode }
                        );
                        replyReminderRef.current!.hide();
                        publish('DialogueTool', { type: 'hideStop' });
                        if (newestLucaAskChatId) {
                            disableOcrEditOrReplace();
                            !lucaAskChatId && updateQaConfig({ lucaAskChatId: newestLucaAskChatId });
                            isCreateQa && delayExcuteCallback(() => updateQaConfig({ refreshCommRecordsByEffect: getUuid() }), 100);
                            mode === 'subject_search' && clickSubjectSearchButtonSensors({ lucaAskChatId: newestLucaAskChatId });
                        }
                        if (success && !isCreateQa) {
                            updateQaConfig({ lucaQuestionAgainNum: lucaQuestionAgainNum - 1 });
                        }
                        disconnectStreamReason.current = undefined;
                        clearLockStatus();
                        isContinueExcute(excuteId) && (await questionSearchProcessFn?.(saveQuestionSearch));
                        isContinueExcute(excuteId) && (await guessYouAskProcessFn?.(saveGuessYouAsk));
                        isLastAskAgain && (await showLastQaTips());
                        isContinueExcute(excuteId) && newestLucaAskChatId && updateQaConfig({ showWhetherSolve: true });
                        deleteExcuteRecord(excuteId);
                    }
                    break;
                case 'stop':
                    disconnectStreamReason.current = 'initiative';
                    streamReader.current?.cancel();
                    break;
                case 'searchSimilarQuestion':
                case 'subjectSearch':
                    {
                        setLockStatus('replying');
                        replyReminderRef.current!.showLoading('analysising');
                        const { success, message, isCreateQa, newestLucaAskChatId, isLastQaAgain, noAskAgainNum, records } =
                            await createQaRecord(params);
                        replyReminderRef.current!.hide();
                        if (success) {
                            records[0]?.sensorsData && createSubjectSearchCardSensors(records[0].sensorsData);
                            const firstItem = records[0];
                            if (firstItem.type === 'streamTips') {
                                await replyReminderRef.current!.simulateStreamReply(firstItem.content);
                            }
                            pushQaRecords(records);
                            await delay(500); // 延时等待动效执行完成
                            updateQaConfig({
                                lucaAskChatId: newestLucaAskChatId,
                                lucaQuestionAgainNum: isCreateQa ? lucaQuestionAgainNum : lucaQuestionAgainNum - 1,
                            });
                            isCreateQa && delayExcuteCallback(() => updateQaConfig({ refreshCommRecordsByEffect: getUuid() }), 100);
                            isLastQaAgain && (await showLastQaTips());
                        } else if (noAskAgainNum) {
                            updateQaConfig({ lucaQuestionAgainNum: 0 });
                            deleteLastQaRecord();
                            await showLastQaTips(message);
                        } else {
                            deleteLastQaRecord();
                            publish('DialogueTool', { type: 'echo', content: params.text.content, images: params.images });
                        }
                        if (newestLucaAskChatId) {
                            updateQaConfig({ showWhetherSolve: true });
                        }
                        clearLockStatus();
                    }
                    break;
                case 'searchQuestionsByOcrContent':
                    {
                        setLockStatus('replying');
                        const { success, newestLucaAskChatId, noAskAgainNum, isCreateQa, guessYouAskProcessFn, excuteId } =
                            await createQaRecord(params, { requestFinish: finish });
                        if (success) {
                            updateQaConfig({ lucaAskChatId: newestLucaAskChatId });
                            isCreateQa && delayExcuteCallback(() => updateQaConfig({ refreshCommRecordsByEffect: getUuid() }), 100);
                        } else if (noAskAgainNum) {
                            updateQaConfig({ lucaQuestionAgainNum: 0 });
                        }
                        clearLockStatus();
                        isContinueExcute(excuteId) && (await guessYouAskProcessFn?.());
                        deleteExcuteRecord(excuteId);
                    }
                    break;
                case 'transformToTeacherQa':
                    {
                        setLockStatus('replying');
                        await createQaRecord(params);
                        clearLockStatus();
                    }
                    break;
                case 'sendMessageToTeacher':
                case 'sendMessageToTeacherOfOldSystem':
                    {
                        setLockStatus('replying');
                        const { success, needDeleteQuestion = true } =
                            type === 'sendMessageToTeacherOfOldSystem'
                                ? await createQaRecordOfOldSystem(params)
                                : await createQaRecord(params);
                        if (success) {
                            updateQaConfig({ questionState: 4, teacherQuestionAgainNum: teacherQuestionAgainNum - 1 });
                        } else if (needDeleteQuestion) {
                            deleteQaRecordByRecordId(recordId);
                            publish('DialogueTool', { type: 'echo', content: params.text.content, images: params.images });
                        }
                        clearLockStatus();
                    }
                    break;
                case 'replaceOcrResult':
                    {
                        setLockStatus('replying');
                        const describe = qaConfig.notQuestionTipsContent || '';
                        const [{ guessYouAskProcessFn, excuteId }] = await Promise.all([
                            createQaRecord(params, { needErrorHandle: true }),
                            replyReminderRef.current!.simulateStreamReply(describe),
                        ]);
                        pushQaRecords({
                            recordId: createQaRecordId('respondent'),
                            role: 'respondent',
                            type: 'streamTips',
                            content: describe,
                        });
                        clearLockStatus();
                        isContinueExcute(excuteId) && (await guessYouAskProcessFn?.());
                        deleteExcuteRecord(excuteId);
                    }
                    break;
                default:
                    preventFunctionCall.current = true;
                    if (streamReader.current) {
                        disconnectStreamReason.current = 'switchChat';
                        streamReader.current.cancel();
                        streamReader.current = undefined;
                    }
                    if (abortController.current) {
                        abortController.current.abort();
                        abortController.current = undefined;
                    }
                    replyReminderRef.current?.hide();
                    break;
            }
        });
    }, [qaConfig]);

    useEffect(() => {
        if (qaRecords.length || showWhetherSolve) {
            handleScrollToBottomByDebounce();
        }
    }, [qaRecords, showWhetherSolve]);

    return (
        <div className="dialogue-area">
            <div className="qa-record-scroll-container" ref={scrollContainerRef}>
                <RenderQaRecord list={qaRecords} />
                <ReplyReminder ref={replyReminderRef} onScrollToBottom={handleScrollToBottomByThrottle} />
                {showWhetherSolve && (
                    <AnimationContainer type="fade_in">
                        <WhetherSolve title={whetherSolveTitle} onSolve={handleSolve} />
                    </AnimationContainer>
                )}
                {questionState === 4 && (
                    <AnimationContainer className="wait-teacher-reply" type="fade_in">
                        <div className="wait-teacher-reply__title">老师回复中...</div>
                        <div className="wait-teacher-reply__tips">
                            {teacherAnswerTime ? `老师预计在 ${teacherAnswerTime} 小时内回答` : ''}
                        </div>
                        {showCreateNewQaBtn && (
                            <div className="wait-teacher-reply__tips">
                                <span>若有其他疑问请点</span>
                                <NewCreateQuestionSvg
                                    className="wait-teacher-reply__create-new-question-icon"
                                    onClick={createNewQuestion}
                                />
                                <span className="wait-teacher-reply__create-new-question" onClick={createNewQuestion}>
                                    新问题
                                </span>
                            </div>
                        )}
                    </AnimationContainer>
                )}
                {questionState === 5 && (
                    <QuestionComment
                        status={appraiseState}
                        value={appraise?.starLevel}
                        lucaAskChatId={lucaAskChatId}
                        askId={askId}
                        handleSubmitComment={handleAppraiseSuccess}
                        onScrollToBottom={handleScrollToBottomByDebounce}
                    />
                )}
            </div>
        </div>
    );
};

export default memo(DialogueArea);
